import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Mail, Phone, Lock, Save, Loader2, Shield } from 'lucide-react';
import EmailPreferencesCard from '../components/EmailPreferencesCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesAgentSettings() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPw, setChangingPw] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${API}/auth/profile`, { headers });
        setProfile({ name: res.data.name || '', email: res.data.email || '', phone: res.data.phone || '' });
      } catch (err) {
        toast.error('Failed to load profile');
      } finally { setLoading(false); }
    };
    if (token) loadProfile();
  }, [token]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, profile, { headers });
      setProfile({ name: res.data.name || '', email: res.data.email || '', phone: res.data.phone || '' });
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPw(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      }, { headers });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPw(false); }
  };

  const basePath = user?.role === 'sales_agent' ? '/sales' : '/admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(basePath)} className="text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="settings-title">Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your profile and account</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-muted/60 border-border mb-6" data-testid="profile-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-indigo-400" /> Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="Your name"
                  data-testid="profile-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="your@email.com"
                  data-testid="profile-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="(555) 123-4567"
                  data-testid="profile-phone"
                />
              </div>
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-profile">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Profile
            </Button>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="bg-muted/60 border-border mb-6" data-testid="password-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="h-5 w-5 text-amber-400" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={passwords.current_password}
                  onChange={(e) => setPasswords(p => ({ ...p, current_password: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="Enter current password"
                  data-testid="current-password"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">New Password</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={passwords.new_password}
                  onChange={(e) => setPasswords(p => ({ ...p, new_password: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="Min 6 characters"
                  data-testid="new-password"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="password"
                  value={passwords.confirm_password}
                  onChange={(e) => setPasswords(p => ({ ...p, confirm_password: e.target.value }))}
                  className="pl-10 bg-background border-border"
                  placeholder="Confirm new password"
                  data-testid="confirm-password"
                />
              </div>
            </div>
            <Button onClick={handleChangePassword} disabled={changingPw} variant="outline" data-testid="change-password">
              {changingPw ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Change Password
            </Button>
          </CardContent>
        </Card>

        {/* Email Preferences */}
        <EmailPreferencesCard token={token} />
      </div>
    </div>
  );
}
